import React, { useState } from "react";
import { Trophy, AlertTriangle, ArrowRight, Bell } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  tenantScoreboard,
  openLoops,
  loopTrend,
  loopTrendTenants,
} from "../data/execData";
import { TenantDetailPanel } from "../components/TenantDetailPanel";

const STAGE_LABELS = ["Incident", "8D", "FMEA", "Ticket"];

const stageColor = (s) =>
  s === "done" ? "#27ae60" : s === "in-progress" ? "#f3b14a" : "#636880";

const PipelineDots = ({ pipeline }) => (
  <div className="flex items-center gap-1.5">
    {pipeline.map((p, i) => (
      <React.Fragment key={i}>
        <span
          title={STAGE_LABELS[i]}
          style={{
            width: 12,
            height: 12,
            borderRadius: "50%",
            background:
              p === "green"
                ? "#27ae60"
                : p === "amber"
                ? "#f3b14a"
                : "rgba(99,104,128,0.5)",
            border: p === "grey" ? "1.5px solid #636880" : "none",
          }}
        />
        {i < 3 && (
          <span
            style={{
              width: 18,
              height: 1,
              background: pipeline[i] === "green" ? "#27ae60" : "rgba(255,255,255,0.10)",
            }}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

const KPI = ({ label, value, valueColor, sub, accent }) => (
  <div
    className="rounded-md"
    style={{
      background: "#111420",
      border: "1px solid rgba(255,255,255,0.12)",
      padding: 20,
    }}
    data-testid={`exec-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}
  >
    <div
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 10,
        letterSpacing: "1.6px",
        color: "#9198b0",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
    <div
      className="mt-3"
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 36,
        color: valueColor,
        fontWeight: 500,
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div
      className="mt-2"
      style={{
        fontFamily: "'DM Mono', monospace",
        fontSize: 11,
        color: accent || "#9198b0",
        lineHeight: 1.4,
      }}
    >
      {sub}
    </div>
  </div>
);

const TenantCard = ({ t, onClick }) => {
  const c =
    t.loopCompletion >= 85 ? "#27ae60" : t.loopCompletion >= 50 ? "#f3b14a" : "#E24B4A";
  return (
    <button
      onClick={onClick}
      className="row-hover w-full text-left"
      style={{
        background: "#111420",
        border: "1px solid rgba(255,255,255,0.12)",
        borderRadius: 10,
        padding: 20,
        marginBottom: 10,
      }}
      data-testid={`exec-tenant-card-${t.name.replace(/[^\w]/g, "-")}`}
    >
      <div className="flex items-center gap-4">
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 24,
            fontWeight: 700,
            color: "#f3b14a",
            minWidth: 36,
            textAlign: "center",
          }}
        >
          #{t.rank}
        </div>
        <div
          className="rounded-md flex items-center justify-center flex-shrink-0"
          style={{
            width: 42,
            height: 42,
            background: "rgba(24,95,165,0.18)",
            border: "1px solid rgba(24,95,165,0.5)",
            color: "#9ec3ec",
            fontFamily: "'DM Mono', monospace",
            fontSize: 12,
          }}
        >
          {t.initials}
        </div>
        <div className="flex-1 min-w-0">
          <div
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 700,
              fontSize: 15,
              color: "#f0f2f8",
            }}
          >
            {t.name}
          </div>
          <div
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
              color: "#9198b0",
              marginTop: 2,
              letterSpacing: "0.4px",
            }}
          >
            {t.type}
          </div>
        </div>
        <div
          style={{
            fontFamily: "'DM Mono', monospace",
            fontSize: 26,
            color: c,
            fontWeight: 500,
            minWidth: 80,
            textAlign: "right",
          }}
        >
          {t.loopCompletion}%
        </div>
      </div>
      <div
        className="mt-3 relative rounded-full overflow-hidden"
        style={{ height: 6, background: "#181c2a" }}
      >
        <div
          className="absolute top-0 bottom-0 left-0 rounded-full"
          style={{
            width: `${t.loopCompletion}%`,
            background: c,
            transition: "width 800ms ease",
          }}
        />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
        {[
          { l: "S1/S2", v: `${t.s1s2.closed}/${t.s1s2.total}` },
          { l: "8D Closed", v: `${t.eightD.closed}/${t.eightD.total}` },
          { l: "FMEA OK", v: `${t.fmea.approved}/${t.fmea.total}` },
          { l: "Avg Days", v: t.avgDays },
        ].map((m) => (
          <div key={m.l}>
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 10,
                color: "#9198b0",
                letterSpacing: "1.2px",
                textTransform: "uppercase",
              }}
            >
              {m.l}
            </div>
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: 14,
                color: "#f0f2f8",
                marginTop: 2,
              }}
            >
              {m.v}
            </div>
          </div>
        ))}
        <div className="flex items-end pb-1">
          <PipelineDots pipeline={t.pipeline} />
        </div>
      </div>
    </button>
  );
};

const riskStyle = (r) => {
  if (r === "high") return { bg: "rgba(226,75,74,0.14)", border: "rgba(226,75,74,0.55)", color: "#ff7e7c", label: "HIGH" };
  if (r === "medium") return { bg: "rgba(243,156,18,0.13)", border: "rgba(243,156,18,0.5)", color: "#f3b14a", label: "MEDIUM" };
  return { bg: "rgba(39,174,96,0.13)", border: "rgba(39,174,96,0.5)", color: "#4ed084", label: "LOW" };
};

export default function Exec() {
  const [tenantFilter, setTenantFilter] = useState("all");
  const [activeTenant, setActiveTenant] = useState(null);

  const filtered =
    tenantFilter === "all" ? tenantScoreboard : tenantScoreboard.filter((t) => t.name === tenantFilter);

  return (
    <div data-testid="exec-view">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-7">
        <div>
          <h1
            style={{
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 800,
              fontSize: "clamp(26px, 3.4vw, 36px)",
              color: "#f0f2f8",
              letterSpacing: "-0.5px",
              lineHeight: 1.05,
            }}
            data-testid="view-title"
          >
            Executive Overview
          </h1>
          <div
            className="mt-1.5 font-dm-mono"
            style={{ fontSize: 12, color: "#9198b0", letterSpacing: "0.4px" }}
          >
            Supplier Quality Loop · BMW Cluster · Last 90 days
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <select
            value={tenantFilter}
            onChange={(e) => setTenantFilter(e.target.value)}
            className="px-3 py-2 rounded-md"
            style={{
              background: "#181c2a",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#f0f2f8",
              fontFamily: "'DM Mono', monospace",
              fontSize: 12,
            }}
            data-testid="exec-tenant-filter"
          >
            <option value="all" style={{ background: "#0d0f18" }}>All tenants</option>
            {tenantScoreboard.map((t) => (
              <option key={t.name} value={t.name} style={{ background: "#0d0f18" }}>{t.name}</option>
            ))}
          </select>
          <button
            className="px-3 py-2 rounded-md"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "#c5cad8",
              fontFamily: "'Syne', system-ui, sans-serif",
              fontWeight: 600,
              fontSize: 12,
            }}
            data-testid="exec-export-pdf-btn"
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KPI
          label="Loop Completion Rate"
          value="67%"
          valueColor="#f3b14a"
          sub="% of S1/S2 incidents with closed 8D + approved FMEA + resolved ticket"
          accent="#f3b14a"
        />
        <KPI
          label="Avg Loop Duration"
          value="12.4d"
          valueColor="#f3b14a"
          sub="Incident open → Jira ticket resolved · target ≤7d"
        />
        <KPI
          label="Open Loops"
          value="4"
          valueColor="#ff7e7c"
          sub="S1/S2 incidents without completed quality cycle"
          accent="#ff7e7c"
        />
        <KPI
          label="Best Performer"
          value="BMW JIS"
          valueColor="#4ed084"
          sub="100% loop completion · avg 3.2 days · congratulations"
          accent="#4ed084"
        />
      </div>

      {/* Scoreboard */}
      <div className="flex items-center gap-2 mb-3">
        <Trophy size={16} color="#f3b14a" />
        <div
          style={{
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 16,
            color: "#f0f2f8",
            letterSpacing: "0.3px",
          }}
        >
          Tenant Loop-Completion Scoreboard
        </div>
      </div>
      <div className="mb-6">
        {filtered.map((t) => (
          <TenantCard key={t.name} t={t} onClick={() => setActiveTenant(t)} />
        ))}
      </div>

      {/* Trend chart */}
      <div className="flex items-center gap-2 mb-3">
        <div
          style={{
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 16,
            color: "#f0f2f8",
            letterSpacing: "0.3px",
          }}
        >
          Loop Completion Trend — 12 weeks
        </div>
      </div>
      <div
        className="rounded-md mb-6"
        style={{
          background: "#111420",
          border: "1px solid rgba(255,255,255,0.12)",
          padding: 20,
        }}
      >
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={loopTrend} margin={{ top: 10, right: 10, left: -16, bottom: 10 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
              <XAxis
                dataKey="week"
                tick={{ fill: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 11 }}
                axisLine={{ stroke: "rgba(255,255,255,0.12)" }}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: "#9198b0", fontFamily: "'DM Mono', monospace", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{
                  background: "#0d0f18",
                  border: "1px solid rgba(255,255,255,0.18)",
                  borderRadius: 6,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11,
                  color: "#f0f2f8",
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "'DM Mono', monospace", fontSize: 11, color: "#c5cad8" }} />
              {loopTrendTenants.map((t) => (
                <Line
                  key={t.name}
                  type="monotone"
                  dataKey={t.name}
                  stroke={t.color}
                  strokeWidth={2.2}
                  dot={{ r: 3, fill: t.color }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Open Loops table */}
      <div className="flex items-center gap-2 mb-3">
        <AlertTriangle size={16} color="#ff7e7c" />
        <div
          style={{
            fontFamily: "'Syne', system-ui, sans-serif",
            fontWeight: 700,
            fontSize: 16,
            color: "#f0f2f8",
            letterSpacing: "0.3px",
          }}
        >
          Open Quality Loops — Action Required
        </div>
      </div>
      <div
        className="rounded-md overflow-x-auto"
        style={{ background: "#111420", border: "1px solid rgba(255,255,255,0.12)" }}
        data-testid="exec-open-loops-table"
      >
        <div style={{ minWidth: 900 }}>
          <div
            className="grid items-center gap-3 px-4 py-3"
            style={{
              gridTemplateColumns: "160px 150px 130px 1fr 110px 100px 160px",
              fontFamily: "'DM Mono', monospace",
              fontSize: 10,
              letterSpacing: "1.5px",
              color: "#9198b0",
              textTransform: "uppercase",
              borderBottom: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <div>Tenant</div>
            <div>Incident</div>
            <div>Opened</div>
            <div>Stage Stalled At</div>
            <div>Days</div>
            <div>Risk</div>
            <div>Action</div>
          </div>
          {openLoops.map((l) => {
            const r = riskStyle(l.risk);
            return (
              <div
                key={l.incident + l.tenant}
                className="grid items-center gap-3 px-4"
                style={{
                  gridTemplateColumns: "160px 150px 130px 1fr 110px 100px 160px",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  minHeight: 56,
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  color: "#c5cad8",
                }}
                data-testid={`exec-open-loop-${l.incident}`}
              >
                <div style={{ fontFamily: "'Syne', system-ui, sans-serif", fontWeight: 700, fontSize: 13, color: "#f0f2f8" }}>{l.tenant}</div>
                <div style={{ color: "#9ec3ec" }}>{l.incident}</div>
                <div>{l.opened}</div>
                <div>{l.stage}</div>
                <div style={{ color: l.daysStalled > 10 ? "#ff7e7c" : l.daysStalled >= 5 ? "#f3b14a" : "#4ed084" }}>
                  {l.daysStalled}d
                </div>
                <span
                  style={{
                    background: r.bg, border: `1px solid ${r.border}`, color: r.color,
                    fontFamily: "'DM Mono', monospace", fontSize: 10, padding: "2px 8px",
                    borderRadius: 3, letterSpacing: "1px", justifySelf: "start",
                  }}
                >
                  {r.label}
                </span>
                <button
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md"
                  style={{
                    background: l.risk === "high" ? "rgba(226,75,74,0.10)" : "rgba(24,95,165,0.14)",
                    border: `1px solid ${l.risk === "high" ? "rgba(226,75,74,0.5)" : "rgba(24,95,165,0.5)"}`,
                    color: l.risk === "high" ? "#ff7e7c" : "#9ec3ec",
                    fontFamily: "'Syne', system-ui, sans-serif",
                    fontWeight: 600,
                    fontSize: 11,
                    justifySelf: "start",
                  }}
                  data-testid={`exec-action-btn-${l.incident}`}
                >
                  {l.action.includes("→") ? <ArrowRight size={11} /> : <Bell size={11} />}
                  {l.action.replace(" →", "")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <TenantDetailPanel tenant={activeTenant} onClose={() => setActiveTenant(null)} />
    </div>
  );
}
